import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { api, pdfUrl } from "../lib/api";
import { money } from "../lib/format";
import { FileText, FileDown, FileCode, ShieldCheck, Plus, Trash2, Save, RotateCcw, Printer } from "lucide-react";
import CompanyBreadcrumb from "../components/CompanyBreadcrumb";
import T4YearEndPanel from "../components/T4YearEndPanel";
import { useClientPerms } from "../lib/useClientPerms";
import { toast } from "sonner";
import { apiErr } from "../lib/api";

const BOXES = [
  ["14", "Employment income", "box_14_employment_income"],
  ["16", "Employee's CPP", "box_16_cpp"],
  ["16A", "Employee's CPP2", "box_16a_cpp2"],
  ["18", "Employee's EI", "box_18_ei"],
  ["22", "Income tax deducted", "box_22_income_tax"],
  ["24", "EI insurable earnings", "box_24_ei_insurable"],
  ["26", "CPP pensionable earnings", "box_26_cpp_pensionable"],
];

export default function T4Slips() {
  const location = useLocation();
  const [companies, setCompanies] = useState([]);
  const [companyId, setCompanyId] = useState("");
  const [employees, setEmployees] = useState([]);
  const { canEdit: t4CanEdit, isClient: t4IsClient } = useClientPerms();
  const t4Edit = t4CanEdit("t4");
  const [year, setYear] = useState(new Date().getFullYear());
  const [selected, setSelected] = useState(null);
  const [t4, setT4] = useState(null);
  const [validation, setValidation] = useState(null);
  const [validating, setValidating] = useState(false);
  const [schema, setSchema] = useState(null);
  const [t4sum, setT4sum] = useState(null);
  const [amended, setAmended] = useState(false);
  const [editBoxes, setEditBoxes] = useState({});
  const [otherInfo, setOtherInfo] = useState([]);
  const [savingT4, setSavingT4] = useState(false);
  const amQ = amended ? "&amended=true" : "";

  const initEdit = (data) => {
    const eb = {};
    BOXES.forEach(([, , key]) => { eb[key] = data[key] ?? 0; });
    setEditBoxes(eb);
    setOtherInfo((data.other_info || []).map((o) => ({ label: o.label, amount: o.amount })));
  };

  const loadSummary = async () => {
    if (!companyId) return;
    try {
      const { data } = await api.get(`/t4-filing/${companyId}/summary?year=${year}`);
      setT4sum(data);
    } catch (e) { toast.error(apiErr(e)); }
  };

  useEffect(() => {
    api.get("/t4-filing/schema-status").then((r) => setSchema(r.data)).catch(() => {});
  }, []);

  const runValidation = async () => {
    if (!companyId) return;
    setValidating(true);
    setValidation(null);
    try {
      const { data } = await api.get(`/t4-filing/${companyId}/validate?year=${year}`);
      setValidation(data);
      if (data.valid) toast.success("Validation passed — ready to file");
      else toast.error(`${data.error_count} issue(s) found`);
    } catch (e) {
      toast.error(apiErr(e));
    } finally {
      setValidating(false);
    }
  };

  useEffect(() => {
    api.get("/companies").then((r) => {
      setCompanies(r.data);
      const qs = new URLSearchParams(location.search).get("company");
      setCompanyId(qs || r.data[0]?.company_id || "");
    });
  }, [location.search]);

  useEffect(() => {
    if (!companyId) return;
    setSelected(null); setT4(null);
    api.get(`/employees?company_id=${companyId}`).then((r) => setEmployees(r.data));
  }, [companyId]);

  const loadT4 = (emp) => {
    setSelected(emp);
    api.get(`/t4/${companyId}/${emp.employee_id}?year=${year}`).then((r) => { setT4(r.data); initEdit(r.data); });
  };

  const saveOverride = async () => {
    if (!selected) return;
    setSavingT4(true);
    try {
      const boxes = {};
      Object.entries(editBoxes).forEach(([k, v]) => { boxes[k] = v === "" ? 0 : Number(v); });
      const other_info = otherInfo.filter((o) => (o.label || "").trim()).map((o) => ({ label: o.label, amount: Number(o.amount) || 0 }));
      const { data } = await api.put(`/t4/${companyId}/${selected.employee_id}/override`, { year, boxes, other_info });
      setT4(data); initEdit(data);
      toast.success("T4 overrides saved");
      if (t4sum) loadSummary();
    } catch (e) { toast.error(apiErr(e)); }
    finally { setSavingT4(false); }
  };

  const resetOverride = async () => {
    if (!selected) return;
    if (!window.confirm("Reset this T4 to its calculated values? This removes all manual overrides and added boxes.")) return;
    setSavingT4(true);
    try {
      const { data } = await api.delete(`/t4/${companyId}/${selected.employee_id}/override?year=${year}`);
      setT4(data); initEdit(data);
      toast.success("Reset to calculated values");
      if (t4sum) loadSummary();
    } catch (e) { toast.error(apiErr(e)); }
    finally { setSavingT4(false); }
  };

  const company = companies.find((c) => c.company_id === companyId);

  return (
    <div className="w-full">
      <CompanyBreadcrumb label="T4 Slips" />
      <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
        <div>
          <h1 className="text-4xl font-black tracking-tight">T4 Slips</h1>
          <p className="text-sm text-muted-foreground mt-1 font-mono uppercase tracking-widest">Statement of Remuneration Paid</p>
          {schema && (
            <span data-testid="schema-status-badge" className={`mt-2 inline-flex items-center gap-1.5 border px-2 py-1 text-[10px] font-mono uppercase tracking-widest ${schema.official_active ? "border-emerald-600 text-emerald-700 bg-emerald-50" : "border-neutral-400 text-neutral-600 bg-neutral-50"}`}>
              <ShieldCheck className="h-3 w-3" />
              {schema.official_active ? "Official CRA schema" : "CRA-aligned schema"} · {schema.schema_file}
            </span>
          )}
        </div>
        <div className="flex gap-2">
          <input data-testid="t4-year-input" type="number" value={year} onChange={(e) => { setYear(Number(e.target.value)); setT4(null); setSelected(null); }} className="w-24 border border-input bg-white px-3 py-2.5 text-sm text-right tabular-nums focus:outline-none focus:ring-2 focus:ring-black" />
          <button data-testid="t4-validate-button" onClick={runValidation} disabled={!companyId || validating} className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium transition-colors whitespace-nowrap border ${companyId ? "border-black bg-white hover:bg-black hover:text-white" : "border-neutral-300 text-neutral-400 pointer-events-none"}`}>
            <ShieldCheck className="h-4 w-4" /> {validating ? "Checking…" : "Validate for CRA"}
          </button>
          <button data-testid="t4-summary-button" onClick={loadSummary} disabled={!companyId} className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium transition-colors whitespace-nowrap border ${companyId ? "border-black bg-white hover:bg-black hover:text-white" : "border-neutral-300 text-neutral-400 pointer-events-none"}`}>
            <FileText className="h-4 w-4" /> T4 Summary
          </button>
          <label data-testid="t4-amended-toggle" className={`flex items-center gap-2 px-3 py-2.5 text-sm font-medium border cursor-pointer select-none whitespace-nowrap ${amended ? "border-red-600 bg-red-50 text-red-700" : "border-input bg-white"}`}>
            <input type="checkbox" checked={amended} onChange={(e) => setAmended(e.target.checked)} className="h-4 w-4 accent-red-600" />
            Amended (type A)
          </label>
          <a data-testid="t4-batch-print-button" href={companyId ? pdfUrl(`/t4-filing/${companyId}/slips.pdf?year=${year}${amQ}`) : undefined} onClick={(e) => { if (!companyId) e.preventDefault(); }} target="_blank" rel="noreferrer" aria-disabled={!companyId} className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium transition-colors whitespace-nowrap border ${companyId ? "border-black bg-white hover:bg-black hover:text-white" : "border-neutral-300 text-neutral-400 pointer-events-none"}`}>
            <Printer className="h-4 w-4" /> Print all T4 slips
          </a>
          <a data-testid="t4-xml-export-button" href={companyId ? pdfUrl(`/t4-filing/${companyId}/xml?year=${year}${amQ}`) : undefined} onClick={(e) => { if (!companyId) e.preventDefault(); }} target="_blank" rel="noreferrer" aria-disabled={!companyId} className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium transition-colors whitespace-nowrap ${companyId ? "bg-black text-white hover:bg-neutral-800" : "bg-neutral-300 text-neutral-500 pointer-events-none"}`}>
            <FileCode className="h-4 w-4" /> Export CRA XML
          </a>
        </div>
      </div>

      <T4YearEndPanel companyId={companyId} year={year} />

      {validation && (
        <div data-testid="t4-validation-panel" className={`border mb-4 ${validation.valid ? "border-emerald-600 bg-emerald-50" : "border-red-600 bg-red-50"}`}>
          <div className="px-5 py-3 flex items-center gap-2 border-b border-black/10">
            <ShieldCheck className={`h-4 w-4 ${validation.valid ? "text-emerald-600" : "text-red-600"}`} />
            <span className="font-bold tracking-tight">
              {validation.valid ? "Ready to file — no errors" : `${validation.error_count} error(s) must be fixed before filing`}
            </span>
            <span className="ml-auto text-xs font-mono text-muted-foreground">{validation.slip_count} slip(s) · {validation.warning_count} warning(s)</span>
          </div>
          {(validation.errors?.length > 0 || validation.warnings?.length > 0) && (
            <ul className="px-5 py-3 space-y-1.5 text-sm">
              {validation.errors?.map((er, i) => (
                <li key={`e${i}`} data-testid={`validation-error-${i}`} className="text-red-700 flex gap-2"><span className="font-mono text-xs mt-0.5">ERR</span> {er}</li>
              ))}
              {validation.warnings?.map((wn, i) => (
                <li key={`w${i}`} className="text-amber-700 flex gap-2"><span className="font-mono text-xs mt-0.5">WARN</span> {wn}</li>
              ))}
            </ul>
          )}
        </div>
      )}

      {t4sum && (
        <div data-testid="t4-summary-panel" className="border border-black bg-white mb-4">
          <div className="px-5 py-3 border-b border-border flex flex-wrap items-center gap-2">
            <FileText className="h-4 w-4" />
            <span className="font-bold tracking-tight">T4 Summary — {t4sum.year}</span>
            <span className="text-xs font-mono text-muted-foreground">{t4sum.slip_count} slip(s)</span>
            <a data-testid="t4-summary-pdf-button" href={pdfUrl(`/t4-filing/${companyId}/summary.pdf?year=${year}${amQ}`)} target="_blank" rel="noreferrer" className="ml-auto flex items-center gap-2 bg-black text-white px-4 py-2 text-sm font-medium hover:bg-neutral-800 transition-colors">
              <FileDown className="h-4 w-4" /> Download PDF
            </a>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4">
            {[
              ["Employment income (14)", t4sum.employment_income],
              ["Employees' CPP (16)", t4sum.cpp],
              ["Employees' CPP2 (16A)", t4sum.cpp2],
              ["Employees' EI (18)", t4sum.ei],
              ["Income tax (22)", t4sum.income_tax],
              ["Employer CPP (27)", t4sum.employer_cpp],
              ["Employer EI (19)", t4sum.employer_ei],
              ["Total remittance (82)", t4sum.total_remittance],
            ].map(([label, val]) => (
              <div key={label} className="border-b border-r border-border p-4">
                <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">{label}</div>
                <div className="mt-1 text-lg font-black tabular-nums">{money(val)}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="grid lg:grid-cols-3 gap-4">
        <div className="border border-border bg-white lg:col-span-1">
          <div className="px-5 py-4 border-b border-border font-bold tracking-tight">Employees</div>
          {employees.length ? employees.map((e) => (
            <button key={e.employee_id} data-testid={`t4-employee-${e.employee_id}`} onClick={() => loadT4(e)} className={`w-full text-left px-5 py-3 border-b border-border last:border-0 text-sm hover:bg-muted transition-colors ${selected?.employee_id === e.employee_id ? "bg-black text-white hover:bg-black" : ""}`}>
              <div className="font-medium">{e.first_name} {e.last_name}</div>
              <div className={`text-xs font-mono ${selected?.employee_id === e.employee_id ? "text-white/60" : "text-muted-foreground"}`}>{e.sin ? `***-**-${e.sin.slice(-3)}` : "No SIN"}</div>
            </button>
          )) : <div className="px-5 py-10 text-center text-sm text-muted-foreground">No employees</div>}
        </div>

        <div className="lg:col-span-2">
          {t4 && selected ? (
            <div className="border border-black bg-white" data-testid="t4-preview">
              <div className="flex items-center justify-between px-6 py-4 border-b border-black">
                <div>
                  <div className="flex items-center gap-2">
                    <span className="text-2xl font-black tracking-tight">T4</span>
                    <span className="text-xs font-mono uppercase tracking-widest text-muted-foreground">Tax Year {t4.year}</span>
                  </div>
                  <div className="text-xs text-muted-foreground mt-0.5">Statement of Remuneration Paid</div>
                </div>
                <a data-testid="t4-pdf-button" href={pdfUrl(`/t4/${companyId}/${selected.employee_id}/pdf?year=${year}${amQ}`)} target="_blank" rel="noreferrer" className="flex items-center gap-2 bg-black text-white px-4 py-2.5 text-sm font-medium hover:bg-neutral-800 transition-colors">
                  <FileDown className="h-4 w-4" /> Download PDF
                </a>
              </div>
              <div className="px-6 py-4 border-b border-border text-sm">
                <div className="grid sm:grid-cols-2 gap-2">
                  <div><span className="text-xs font-mono uppercase tracking-widest text-muted-foreground">Employer</span><div className="font-medium">{company?.name}</div></div>
                  <div><span className="text-xs font-mono uppercase tracking-widest text-muted-foreground">Business #</span><div className="font-mono">{company?.cra_business_number || "N/A"}</div></div>
                  <div><span className="text-xs font-mono uppercase tracking-widest text-muted-foreground">Employee</span><div className="font-medium">{selected.first_name} {selected.last_name}</div></div>
                  <div><span className="text-xs font-mono uppercase tracking-widest text-muted-foreground">SIN</span><div className="font-mono">{selected.sin || "N/A"}</div></div>
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2">
                {BOXES.map(([num, label, key]) => {
                  const edited = (t4.overridden || []).includes(key);
                  return (
                    <div key={num} className="border-b border-r border-border p-4">
                      <div className="flex items-center gap-2 text-[10px] font-mono uppercase tracking-widest text-muted-foreground">
                        Box {num} · {label}
                        {edited && <span data-testid={`t4-edited-${key}`} className="text-amber-600 border border-amber-400 px-1 leading-none py-0.5">edited</span>}
                      </div>
                      <div className="mt-1 flex items-center">
                        <span className="text-lg font-black text-muted-foreground mr-1">$</span>
                        <input
                          data-testid={`t4-box-${num}`}
                          type="number" step="0.01"
                          value={editBoxes[key] ?? ""}
                          onChange={(e) => setEditBoxes((b) => ({ ...b, [key]: e.target.value }))}
                          className="w-full text-lg font-black tabular-nums bg-transparent border-b border-dashed border-border focus:outline-none focus:border-black py-0.5"
                        />
                      </div>
                    </div>
                  );
                })}
              </div>

              <div className="px-6 py-4 border-t border-border">
                <div className="flex items-center justify-between">
                  <span className="text-[11px] font-mono uppercase tracking-widest text-muted-foreground">Other information (added boxes)</span>
                  <button data-testid="t4-add-box-button" onClick={() => setOtherInfo((o) => [...o, { label: "", amount: "" }])} className="inline-flex items-center gap-1 text-xs font-medium border border-border px-2.5 py-1 hover:border-black transition-colors">
                    <Plus className="h-3.5 w-3.5" /> Add box
                  </button>
                </div>
                {otherInfo.length > 0 && (
                  <div className="mt-3 space-y-2">
                    {otherInfo.map((o, i) => (
                      <div key={i} className="flex items-center gap-2" data-testid={`t4-otherinfo-row-${i}`}>
                        <input data-testid={`t4-otherinfo-label-${i}`} placeholder="Box code / label" value={o.label} onChange={(e) => setOtherInfo((arr) => arr.map((x, j) => j === i ? { ...x, label: e.target.value } : x))} className="flex-1 border border-input px-2.5 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-black" />
                        <div className="flex items-center">
                          <span className="text-sm text-muted-foreground mr-1">$</span>
                          <input data-testid={`t4-otherinfo-amount-${i}`} type="number" step="0.01" placeholder="0.00" value={o.amount} onChange={(e) => setOtherInfo((arr) => arr.map((x, j) => j === i ? { ...x, amount: e.target.value } : x))} className="w-28 border border-input px-2.5 py-1.5 text-sm text-right tabular-nums focus:outline-none focus:ring-2 focus:ring-black" />
                        </div>
                        <button data-testid={`t4-otherinfo-remove-${i}`} onClick={() => setOtherInfo((arr) => arr.filter((_, j) => j !== i))} className="text-muted-foreground hover:text-red-600 transition-colors"><Trash2 className="h-4 w-4" /></button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div className="px-6 py-4 border-t border-border flex flex-wrap items-center gap-2">
                <button data-testid="t4-save-override-button" onClick={saveOverride} disabled={savingT4 || !t4Edit} className="inline-flex items-center gap-2 bg-black text-white px-5 py-2.5 text-sm font-medium hover:bg-neutral-800 transition-colors disabled:opacity-50">
                  <Save className="h-4 w-4" /> {savingT4 ? "Saving…" : "Save overrides"}
                </button>
                {!t4IsClient && <button data-testid="t4-reset-override-button" onClick={resetOverride} disabled={savingT4 || !t4Edit} className="inline-flex items-center gap-2 border border-border px-5 py-2.5 text-sm font-medium hover:border-black transition-colors disabled:opacity-50">
                  <RotateCcw className="h-4 w-4" /> Reset to calculated
                </button>}
                <span className="text-[11px] text-muted-foreground ml-auto">CRA-instructed overrides prevent amendments — saved values flow into the slip, Summary, PDF & XML.</span>
              </div>
              <div className="px-6 py-3 text-[11px] text-muted-foreground border-t border-border">
                Based on {t4.slip_count} pay statement(s) in {t4.year}. Preview only — file the official T4 with CRA before the deadline.
              </div>
            </div>
          ) : (
            <div className="border border-dashed border-border bg-white h-full min-h-[300px] flex flex-col items-center justify-center text-center p-8">
              <FileText className="h-10 w-10 text-muted-foreground" />
              <p className="mt-4 text-sm text-muted-foreground max-w-xs">Select an employee to generate their T4 slip for {year}.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
